import { RegType, type FullAirHandlerUnit } from "@eisox/api/core-api";
import { Alert, Select as DSSelect, Field, Slider } from "@eisox/design-system";
import { FieldErrors, withForm } from "@eisox/form";

import {
  DEAD_BAND_STEP,
  MAXIMUM_DEAD_BAND,
  MAXIMUM_SUPPLY_AIR_MAX_TEMPERATURE_SETPOINT,
  MAXIMUM_SUPPLY_AIR_MIN_TEMPERATURE_SETPOINT,
  MINIMUM_DEAD_BAND,
  MINIMUM_SUPPLY_AIR_MAX_TEMPERATURE_SETPOINT,
  MINIMUM_SUPPLY_AIR_MIN_TEMPERATURE_SETPOINT,
  STEP,
} from "~/constants/air-handler-unit.constant";
import { m } from "~/paraglide/messages";

import {
  getTemperatureCouples,
  holdAbove,
  holdBelow,
  isCompleteTemperatureCouple,
  SETPOINT_PERIOD_TITLE,
  SETPOINT_SEASONS,
  setpointFormOptions,
  toCoolingSetpoint,
  toHeatingSetpoint,
  toSetpointAndDeadBand,
  widenBoundsTo,
  type Season,
  type TemperatureCouple,
} from "../helpers";
import { DiscrepancyLabel } from "./discrepancy-slider";
import { SetpointBoundField } from "./setpoint-bound-field";
import { SetpointSeasonBlock } from "./setpoint-season-block";

export const SetpointInputMode = {
  SETPOINT: "setpoint",
  DEAD_BAND: "deadBand",
} as const;

export type SetpointInputMode = (typeof SetpointInputMode)[keyof typeof SetpointInputMode];

const getRegTypeHelp = (regType: RegType | undefined) => {
  if (regType === RegType.AMBIENT_SETPOINT) return m.air_handler_unit_setpoints_ambiant_temperature_help();
  if (regType === RegType.RETURN_SETPOINT) return m.air_handler_unit_setpoints_return_air_temperature_help();
  if (regType === RegType.SUPPLY_SETPOINT) return m.air_handler_unit_setpoints_supply_air_temperature_help();
  return undefined;
};

/**
 * `CSG_SOUF_MIN` / `CSG_SOUF_MAX` bound the heating setpoint as much as the cooling one: they head
 * the temperature section as every other quantity heads its own with its bounds.
 *
 * Both modes carry the same four settings per season: switching tab rephrases the couple, it never
 * adds or drops a slider. Under a setpoint the heating side leads, under a dead band the occupation
 * does, the couple reading as one group. The mode is held by the page, so folding the section
 * keeps it.
 */
const SetpointTemperatureSection = withForm({
  ...setpointFormOptions,
  props: {} as {
    airHandlerUnit: FullAirHandlerUnit;
    inputMode: SetpointInputMode;
    onInputModeChange: (inputMode: SetpointInputMode) => void;
  },
  render: function Render({ form, airHandlerUnit, inputMode, onInputModeChange }) {
    const couples = getTemperatureCouples(airHandlerUnit);
    const isComplete = (couple: TemperatureCouple) => isCompleteTemperatureCouple(airHandlerUnit, couple);
    const deadBandAvailable = couples.some(isComplete);
    const deadBandMode = deadBandAvailable && inputMode === SetpointInputMode.DEAD_BAND;
    const regTypeHelp = getRegTypeHelp(airHandlerUnit.regType);

    const seasonCouples = (season: Season) => couples.filter(couple => couple.season === season);

    const heatingField = ({ heating, cooling, period, min, max }: TemperatureCouple) => (
      <form.AppField key={heating} name={heating}>
        {field => {
          const range = widenBoundsTo({ min, max }, field.state.value);

          return (
            <field.Slider
              classNames={{ control: "w-full", value: "w-16 justify-end" }}
              label={
                <DiscrepancyLabel label={`${m.hot()} — ${SETPOINT_PERIOD_TITLE[period]()} (°C)`} property={heating} />
              }
              min={range.min}
              max={range.max}
              step={STEP}
              onValueChange={value => {
                const held = holdBelow(value, form.getFieldValue(cooling), STEP);
                if (held !== undefined && held !== value) field.handleChange(held);
              }}
            />
          );
        }}
      </form.AppField>
    );

    const coolingField = ({ heating, cooling, period, min, max }: TemperatureCouple) => (
      <form.AppField key={cooling} name={cooling}>
        {field => {
          const range = widenBoundsTo({ min, max }, field.state.value);

          return (
            <field.Slider
              classNames={{ control: "w-full", value: "w-16 justify-end" }}
              label={
                <DiscrepancyLabel label={`${m.cold()} — ${SETPOINT_PERIOD_TITLE[period]()} (°C)`} property={cooling} />
              }
              min={range.min}
              max={range.max}
              step={STEP}
              onValueChange={value => {
                const held = holdAbove(value, form.getFieldValue(heating), STEP);
                if (held !== undefined && held !== value) field.handleChange(held);
              }}
            />
          );
        }}
      </form.AppField>
    );

    const deadBandField = ({ heating, cooling, period, min, max }: TemperatureCouple) => (
      <form.AppField key={cooling} name={cooling}>
        {field => (
          <form.Subscribe selector={state => state.values[heating]}>
            {heatingValue => {
              const coolingValue = field.state.value;
              if (heatingValue === undefined || coolingValue === undefined) return null;

              const { setpoint, deadBand } = toSetpointAndDeadBand(heatingValue, coolingValue);
              const room = Math.min(setpoint - min, max - setpoint);

              return (
                <Field.Root className="gap-2.5">
                  <Slider
                    className="w-full"
                    classNames={{ value: "w-16 justify-end" }}
                    label={
                      <DiscrepancyLabel
                        label={`${SETPOINT_PERIOD_TITLE[period]()} — ${m.setpoint()} (°C)`}
                        property={heating}
                      />
                    }
                    min={Math.min(min + deadBand / 2, setpoint)}
                    max={Math.max(max - deadBand / 2, setpoint)}
                    step={STEP}
                    value={setpoint}
                    onValueChange={value => {
                      if (value === undefined) return;
                      form.setFieldValue(heating, toHeatingSetpoint(value, deadBand));
                      field.handleChange(toCoolingSetpoint(value, deadBand));
                    }}
                  />
                  <Slider
                    className="w-full"
                    classNames={{ value: "w-16 justify-end" }}
                    label={`${SETPOINT_PERIOD_TITLE[period]()} — ${m.dead_band()} (°C)`}
                    // A dead band of 0 would put the cooling setpoint on the heating one: the slider
                    // stops a step short, and only opens on 0 to show a couple the automate reports so.
                    min={Math.min(MINIMUM_DEAD_BAND, deadBand)}
                    max={Math.max(Math.min(MAXIMUM_DEAD_BAND, 2 * room), deadBand)}
                    step={DEAD_BAND_STEP}
                    value={deadBand}
                    onValueChange={value => {
                      if (value === undefined) return;
                      form.setFieldValue(heating, toHeatingSetpoint(setpoint, value));
                      field.handleChange(toCoolingSetpoint(setpoint, value));
                    }}
                  />
                  <p className="text-base text-primary-dark-blue">
                    {`${m.heating_setpoint()} : ${heatingValue.toFixed(1)} °C · ${m.cooling_setpoint()} : ${coolingValue.toFixed(1)} °C`}
                  </p>
                  <FieldErrors />
                </Field.Root>
              );
            }}
          </form.Subscribe>
        )}
      </form.AppField>
    );

    const reportedFields = (candidates: TemperatureCouple[]) => [
      ...candidates.filter(({ heating }) => airHandlerUnit[heating] !== undefined).map(heatingField),
      ...candidates.filter(({ cooling }) => airHandlerUnit[cooling] !== undefined).map(coolingField),
    ];

    const seasonFields = (season: Season) =>
      deadBandMode
        ? [
            ...seasonCouples(season).filter(isComplete).map(deadBandField),
            ...reportedFields(seasonCouples(season).filter(couple => !isComplete(couple))),
          ]
        : reportedFields(seasonCouples(season));

    return (
      <div className="gap-2.5 flex flex-col">
        {regTypeHelp && <Alert severity="help">{regTypeHelp}</Alert>}
        {airHandlerUnit.supplyAirMinTemperatureSetpoint !== undefined && (
          <SetpointBoundField
            form={form}
            name="supplyAirMinTemperatureSetpoint"
            other="supplyAirMaxTemperatureSetpoint"
            side="min"
            label={
              <DiscrepancyLabel
                label={`${m.supply_air_min_temperature_setpoint()} (°C)`}
                property="supplyAirMinTemperatureSetpoint"
              />
            }
            range={{
              min: MINIMUM_SUPPLY_AIR_MIN_TEMPERATURE_SETPOINT,
              max: MAXIMUM_SUPPLY_AIR_MIN_TEMPERATURE_SETPOINT,
            }}
            step={STEP}
          />
        )}
        {airHandlerUnit.supplyAirMaxTemperatureSetpoint !== undefined && (
          <SetpointBoundField
            form={form}
            name="supplyAirMaxTemperatureSetpoint"
            other="supplyAirMinTemperatureSetpoint"
            side="max"
            label={
              <DiscrepancyLabel
                label={`${m.supply_air_max_temperature_setpoint()} (°C)`}
                property="supplyAirMaxTemperatureSetpoint"
              />
            }
            range={{
              min: MINIMUM_SUPPLY_AIR_MAX_TEMPERATURE_SETPOINT,
              max: MAXIMUM_SUPPLY_AIR_MAX_TEMPERATURE_SETPOINT,
            }}
            step={STEP}
          />
        )}
        {deadBandAvailable && (
          <Field.Root>
            <Field.Label>{m.air_handler_unit_setpoint_input_mode()}</Field.Label>
            <Field.Control
              render={
                <DSSelect.Root
                  multiple={false}
                  value={inputMode}
                  onValueChange={value => onInputModeChange(value as SetpointInputMode)}
                >
                  <DSSelect.Trigger className="w-full">
                    <DSSelect.Value />
                  </DSSelect.Trigger>
                  <DSSelect.Content>
                    <DSSelect.Viewport>
                      <DSSelect.Item value={SetpointInputMode.SETPOINT}>
                        {m.air_handler_unit_setpoint_input_mode_setpoint()}
                      </DSSelect.Item>
                      <DSSelect.Item value={SetpointInputMode.DEAD_BAND}>
                        {m.air_handler_unit_setpoint_input_mode_dead_band()}
                      </DSSelect.Item>
                    </DSSelect.Viewport>
                  </DSSelect.Content>
                </DSSelect.Root>
              }
            />
          </Field.Root>
        )}
        {SETPOINT_SEASONS.filter(season => seasonCouples(season).length > 0).map(season => (
          <SetpointSeasonBlock key={season} season={season}>
            {seasonFields(season)}
          </SetpointSeasonBlock>
        ))}
      </div>
    );
  },
});

export { SetpointTemperatureSection };
